import { DIET_CONSULTATION_AMOUNT, ZOG_CONSULTATION_AMOUNT } from "./stripe";
import { DiagnosticSale, DiagnosticSaleOf, ZOG_ORDER_URL, diagnosticSale } from "./diagnosticsOffer";
import { DIET_TAB_PATH } from "./dietPage";

/**
 * «Все наши продукты» — один список на весь сайт (PRODUCTS_DESIGN.md, доски PROD_*).
 *
 * Владелец, 28.09.2026: купить ВСЕ наши продукты — 80 % сферы «вклад»; обе
 * диагностики входят в общее число; спрей и семена на ухо — «скоро в продаже».
 * Поэтому «все продукты» должны быть настоящим списком, который можно
 * посчитать: он здесь. Владелец, 03.10.2026: самодиагностика по болезням —
 * часть приложения, а не отдельный продукт (её строка — SelfCheckInAppRow).
 *
 * Только данные и маленькие помощники — без React и без импортов из pages/
 * (shared/ их не импортирует). Цены — числа; тест products.test.ts держит их
 * равными сегодняшним каталогам (courseCards, TOKEN_PACKS, PACKAGES, stripe.ts),
 * так что тихо разъехаться они не могут.
 *
 * НИЧЕГО НЕ ПРОДАЁТ И НЕ НАЧИСЛЯЕТ. «Купить» ведёт только на сегодняшние
 * страницы покупки; у «скоро в продаже» нет ни цены, ни ссылки — цены
 * владелец ещё не назвал, и выдумывать их нельзя (тест это проверяет).
 * Аюрведические продукты центра «Амрита» продаёт их сайт: у них только ссылка
 * туда (externalUrl), без цены и баллов, и в счёт они не входят.
 */

export type ProductKind =
    | "course"
    | "package"
    | "app"
    | "tokens"
    | "specialist"
    | "consultation"
    | "diagnostic"
    | "goods"
    /** Аюрведические продукты центра «Амрита» — продаёт их сайт центра, не мы. */
    | "ayurveda";

export type ProductStatus = "on_sale" | "coming_soon";

/**
 * Группы на экране, в этом порядке: Курсы · Приложение и умный поиск · Диагностика ·
 * Товары для здоровья · Аюрведические продукты · Пакеты (товары и аюрведа пока
 * скрыты — HEALTH_GOODS_SHOWN). Внутренних названий систем («СУП», «ZOG»,
 * «воркер»…) в подписях нет — только слова человека.
 */
export type ProductGroup = "courses" | "app" | "diagnostics" | "goods" | "ayurveda" | "packages";

export type ProductIcon =
    | "course"
    | "guide"
    | "karma"
    | "app"
    | "tokens"
    | "specialist"
    | "diet"
    | "zog"
    | "disease"
    | "consult"
    | "spray"
    | "ear"
    | "herb"
    | "package";

export interface LocalText {
    ru: string;
    en: string;
}

/** Как сайт узнаёт «у вас есть». Только чтение, по одному запросу на источник. */
export type OwnershipProof =
    /** Любой из этих курсов в ownership.json — ГОЛЫЙ набор, никогда не isOwned() (там админская поблажка). */
    | { by: "course"; ids: string[] }
    /** Воркер: GET /account/entitlements → subscriptionActive. */
    | { by: "subscription" }
    /** quota.json: пакет с source "purchase", у которого initialCount — одно из этих чисел. */
    | { by: "quota"; initialCounts: number[] }
    /** Консультация этого вида: оплачена или завершена — есть; начата — в ходе. */
    | { by: "diagnostic"; type: "diet" | "zog" }
    /** Узнать нельзя (скоро в продаже, по записи). */
    | { by: "none" };

export interface Product {
    /** Постоянный, не переиспользуется: модель баллов и аналитика держатся за него. */
    id: string;
    kind: ProductKind;
    group: ProductGroup;
    /** Части, которые рисуются ОДНОЙ карточкой с «таблетками» частей. */
    family?: string;
    /** Подпись «таблетки» внутри карточки семейства («1», «для мужчин»). */
    part?: LocalText;
    title: LocalText;
    /**
     * «Что это даёт» — одна короткая строка простыми словами (из описаний самого
     * сайта). Без медицинских обещаний и без выдумок.
     */
    gives?: LocalText;
    /** Мелкая строка рядом с ценой: формат, где купить («Текст на 5 языках и аудио»). */
    sub?: LocalText;
    /** Цена без скидок в центах USD. null — неизвестна / скоро (тогда не показывается вовсе). */
    listPriceCents: number | null;
    /** «от $15» — цена самой дешёвой ступени лестницы. */
    priceFrom?: boolean;
    /** «$30 за год». */
    priceUnit?: "year";
    /** Цена словами там, где цены списка нет («от $1 550»). */
    priceText?: LocalText;
    /**
     * Своей цены, которую можно заплатить по ссылке, нет (вопросы специалисту — за
     * токены или помесячно на странице специалиста): ни цены, ни чипа,
     * «Подробнее ›» вместо «Купить ›».
     */
    detailsOnly?: boolean;
    status: ProductStatus;
    /**
     * Адрес СЕГОДНЯШНЕЙ страницы покупки без языка; null — скоро в продаже или
     * продаёт чужой сайт (externalUrl).
     */
    buyRoute: string | null;
    /**
     * Продаёт не наш сайт (центр «Амрита»): «Узнать цену и заказать ↗» ведёт туда,
     * в новой вкладке. Цены нет, баллов нет — покупка там до нас не доходит.
     */
    externalUrl?: string;
    /** Фото товара из public/ (адрес от корня сайта) — вместо значка в круге. */
    image?: string;
    /** Куда «Открыть», когда уже есть (курс, результаты…), без языка. */
    openRoute?: string;
    owned: OwnershipProof;
    icon: ProductIcon;
    /**
     * Входит в «все наши продукты» — опорный набор модели баллов. Счёта «N из M» на
     * экране больше нет (владелец, 29.09.2026: страница не должна звать купить всё).
     */
    countable: boolean;
    order: number;
    /** Формат метода (видеокурс или пособие) — см. METHOD_VIDEO_COURSE_IDS ниже. */
    methodFormat?: MethodFormat;
    /** Пакет, в котором есть видеокурс или пособие метода. */
    includesMethod?: boolean;
    /** Отдельно не продаётся — только в этом пакете (id продукта пакета). */
    onlyInPackage?: string;
}

/** Семейство частей одного курса — одна карточка. */
export interface ProductFamily {
    key: string;
    title: LocalText;
    /** «Что это даёт» — одна строка простыми словами (как у Product.gives). */
    gives: LocalText;
    /** Перед ценой в строке под названием («Видеокурс метода»). */
    sub?: LocalText;
    /** «$14.90 за часть» (две части) или «за каждую». */
    priceUnit: "part" | "each";
    /** «5 частей» в строке под названием. */
    showCount?: boolean;
    /** Куда «Открыть», когда есть все части, без языка. */
    openRoute: string;
    /** Формат метода (видеокурс или пособие) — см. METHOD_VIDEO_COURSE_IDS ниже. */
    methodFormat?: MethodFormat;
    /**
     * «Части» семейства — не части, а издания одного курса («для мужчин» / «для
     * женщин»): выбор, а не дополнение. В списках курсов — одна строка.
     */
    alternatives?: boolean;
}

export const PRODUCT_GROUPS: readonly { key: ProductGroup; title: LocalText }[] = [
    { key: "courses", title: { ru: "Курсы", en: "Courses" } },
    { key: "app", title: { ru: "Приложение и умный поиск", en: "App & smart search" } },
    { key: "diagnostics", title: { ru: "Диагностика", en: "Diagnostics" } },
    { key: "goods", title: { ru: "Товары для здоровья", en: "Health goods" } },
    { key: "ayurveda", title: { ru: "Аюрведические продукты", en: "Ayurvedic products" } },
    { key: "packages", title: { ru: "Пакеты", en: "Packages" } },
];

/** Сайт центра «Амрита» (методики О. Г. Торсунова): цены — по запросу, заказ — там. */
export const AMRITA_URL = "https://torsunov-amrita.com/";

export const PRODUCT_FAMILIES: readonly ProductFamily[] = [
    {
        key: "effective-health",
        title: { ru: "«Восстановление жизненных сил»", en: "“Restoring vital energy”" },
        gives: {
            ru: "Основа метода: пять слоёв энергии и как их очистить",
            en: "The core of the method: the five energy layers and how to clear them",
        },
        sub: { ru: "Видеокурс метода", en: "The method's video course" },
        priceUnit: "part",
        openRoute: "/account/my-courses-new/group/effective-health",
        methodFormat: "video",
    },
    {
        key: "what-is-love",
        title: { ru: "«Что такое любовь?»", en: "“What is love?”" },
        gives: {
            ru: "Как рождается и крепнет любовь в семье",
            en: "How love is born and grows stronger in a family",
        },
        priceUnit: "each",
        showCount: true,
        openRoute: "/account/my-courses-new/group/what-is-love",
    },
    {
        key: "motivation",
        title: { ru: "«Мотивационная настройка»", en: "“Motivational attunement”" },
        gives: {
            ru: "Простые правила, которые сохраняют любовь в семье",
            en: "Simple rules that keep love alive in a family",
        },
        priceUnit: "each",
        openRoute: "/account/my-courses-new",
        alternatives: true,
    },
];

const COURSE_PRICE = 1490;
const GUIDE_PRICE = 1990;
const QUICK_START_PRICE = 1990;

/** Часть курса за $14.90: покупка — /buy/<id>, открыть — страница курса. */
const coursePart = (
    courseId: string,
    family: string,
    part: LocalText,
    title: LocalText,
    order: number,
): Product => ({
    id: `course:${courseId}`,
    kind: "course",
    group: "courses",
    family,
    part,
    title,
    listPriceCents: COURSE_PRICE,
    status: "on_sale",
    buyRoute: `/buy/${courseId}`,
    openRoute: `/account/my-courses-new/${courseId}`,
    owned: { by: "course", ids: [courseId] },
    icon: "course",
    countable: true,
    order,
});

const WIL = (n: number): LocalText => ({
    ru: `«Что такое любовь?», часть ${n}`,
    en: `“What is love?”, part ${n}`,
});

/** Все издания пособия — один продукт (правило владельца для баллов обучения). */
export const HEALTH_GUIDE_EDITIONS = [
    "health-guide",
    "health-guide-audio",
    "health-guide-pdf",
    "effective-health-pdf",
    "health-guide-text-ru",
    "health-guide-text-en",
    "health-guide-text-de",
    "health-guide-text-cn",
    "health-guide-text-es",
];

/**
 * ОДИН МЕТОД — РАЗНЫЕ ФОРМАТЫ. Владелец, 29.09.2026: «не надо продавать отдельно
 * пдф курса и видео курса. пдф это для тех кто не хочет курс по каким то причинам
 * (может знаком уже с методикой или еще что то — например после диагностики ЗОЖ
 * человек получает пдф). тоже самое для аудио версии отдельно».
 *
 * Видеокурс «Восстановление жизненных сил» (части) и «Методическое пособие по ЗОЖ»
 * (текст или аудио) — не дополнения друг к другу, а ВЫБОР одного из них. Поэтому:
 * у кого есть один формат, тому другой не предлагаем (ни «Купить», ни цены, ни
 * баллов за покупку, ни пакетов, где метод лежит ещё раз); в новый пакет два
 * формата вместе не кладём. Сегодняшние пакеты с методом (includesMethod) остаются
 * как есть — решение по ним за владельцем.
 */
export type MethodFormat = "video" | "guide";

/** Части видеокурса метода — те же, что в замке кабинета (features/methodCourseGate). */
export const METHOD_VIDEO_COURSE_IDS = ["effective-health", "effective-health-part-2"];

/** Какой формат метода этот курс (id из ownership.json); null — не метод. */
export const methodFormatOfCourse = (courseId: string): MethodFormat | null =>
    METHOD_VIDEO_COURSE_IDS.includes(courseId) ? "video" : HEALTH_GUIDE_EDITIONS.includes(courseId) ? "guide" : null;

export type MethodFormatsOwned = Record<MethodFormat, boolean>;

/** Какие форматы метода у человека уже есть — по ГОЛОМУ набору купленных курсов. */
export const methodFormatsOwned = (has: (courseId: string) => boolean): MethodFormatsOwned => ({
    video: METHOD_VIDEO_COURSE_IDS.some(has),
    guide: HEALTH_GUIDE_EDITIONS.some(has),
});

/** У человека есть ДРУГОЙ формат того же метода — этот ему не предлагаем. */
export const ownsOtherMethodFormat = (format: MethodFormat, owned: MethodFormatsOwned): boolean =>
    format === "video" ? owned.guide : owned.video;

const methodVideo = (p: Product): Product => ({ ...p, methodFormat: "video" });

export const PRODUCTS: readonly Product[] = [
    // ── Курсы (13, считаются по частям) ─────────────────────────────────────
    {
        // «Практика без ошибок» (как на .life): наглядный гид по практикам метода — как
        // выполнять правильно, типичные ошибки, периодичность по слоям. Курс целиком, $19.90;
        // материалы — по-русски на любом языке сайта.
        id: "course:quick-start",
        kind: "course",
        group: "courses",
        title: { ru: "Практика без ошибок", en: "Practice without mistakes" },
        gives: {
            ru: "Как выполнять практики правильно, типичные ошибки и периодичность",
            en: "The method's practices at a glance: how to do them right, common mistakes and frequency",
        },
        sub: { ru: "Слайды для быстрого начала", en: "Slides for a quick start, in Russian" },
        listPriceCents: QUICK_START_PRICE,
        status: "on_sale",
        buyRoute: "/buy/quick-start",
        openRoute: "/account/my-courses-new/quick-start",
        owned: { by: "course", ids: ["quick-start"] },
        icon: "course",
        countable: true,
        order: 5,
    },
    methodVideo(
        coursePart("effective-health", "effective-health", { ru: "Часть 1", en: "Part 1" }, {
            ru: "«Восстановление жизненных сил», часть 1",
            en: "“Restoring vital energy”, part 1",
        }, 10),
    ),
    methodVideo(
        coursePart("effective-health-part-2", "effective-health", { ru: "Часть 2", en: "Part 2" }, {
            ru: "«Восстановление жизненных сил», часть 2",
            en: "“Restoring vital energy”, part 2",
        }, 11),
    ),
    coursePart("happy-family", "what-is-love", { ru: "1", en: "1" }, WIL(1), 20),
    coursePart("happy-family-part-2", "what-is-love", { ru: "2", en: "2" }, WIL(2), 21),
    coursePart("happy-family-part-3", "what-is-love", { ru: "3", en: "3" }, WIL(3), 22),
    coursePart("happy-family-part-4", "what-is-love", { ru: "4", en: "4" }, WIL(4), 23),
    coursePart("happy-family-part-5", "what-is-love", { ru: "5", en: "5" }, WIL(5), 24),
    coursePart("happy-family-intro", "motivation", { ru: "для мужчин", en: "for men" }, {
        ru: "«Мотивационная настройка» — для мужчин",
        en: "“Motivational attunement” — for men",
    }, 30),
    coursePart("happy-family-intro-women", "motivation", { ru: "для женщин", en: "for women" }, {
        ru: "«Мотивационная настройка» — для женщин",
        en: "“Motivational attunement” — for women",
    }, 31),
    {
        id: "course:health-guide",
        kind: "course",
        group: "courses",
        title: { ru: "Методическое пособие по ЗОЖ", en: "Methodical health guide" },
        // Тот же метод, что в видеокурсе, — для тех, кому видеокурс не подходит (владелец, 29.09.2026).
        gives: {
            ru: "Метод текстом или аудио — если видеокурс не для вас",
            en: "The method as text or audio — if the video course isn't for you",
        },
        // Издания — текст на 5 языках, аудио и текст + аудио; видеоиздания нет (courseCards).
        sub: { ru: "Текст на 5 языках и аудио", en: "Text in 5 languages and audio" },
        listPriceCents: GUIDE_PRICE,
        status: "on_sale",
        // Изданий несколько — сначала выбор издания.
        buyRoute: "/account/my-courses-new/group/health-guide",
        openRoute: "/account/my-courses-new/group/health-guide",
        owned: { by: "course", ids: HEALTH_GUIDE_EDITIONS },
        icon: "guide",
        countable: true,
        order: 40,
        methodFormat: "guide",
    },
    {
        id: "course:family-course-part-1",
        kind: "course",
        group: "courses",
        title: { ru: "«Семейный видеокурс», часть 1", en: "“Family video course”, part 1" },
        gives: { ru: "Метод для всей семьи — в видео", en: "The method for the whole family, on video" },
        sub: { ru: "в пакете «Энергия для старта»", en: "in the “Energy for the Start” package" },
        // Отдельно не продаётся — только в пакете «Энергия для старта» ($35.90, баллы — за
        // пакет). Своей цены, которую можно заплатить, нет: ни цены, ни чипа, «Пакеты ›».
        listPriceCents: null,
        status: "on_sale",
        buyRoute: "/packages",
        openRoute: "/account/my-courses-new",
        owned: { by: "course", ids: ["family-course-part-1"] },
        icon: "course",
        countable: true,
        order: 50,
        onlyInPackage: "package:pack-1-starter",
    },
    {
        id: "course:karma",
        kind: "course",
        group: "courses",
        title: { ru: "«Карма в нашей жизни», часть 1", en: "“Karma in our life”, part 1" },
        gives: { ru: "Как устроены законы судьбы — в анимации", en: "How the laws of fate work — shown in animation" },
        listPriceCents: null,
        status: "coming_soon",
        buyRoute: null,
        owned: { by: "course", ids: ["karma"] },
        icon: "karma",
        countable: true,
        order: 60,
    },

    // ── Приложение и умный поиск (3) ────────────────────────────────────────
    {
        id: "app:my-revive-year",
        kind: "app",
        group: "app",
        title: { ru: "MY REVIVE — год", en: "MY REVIVE — 1 year" },
        gives: {
            ru: "Приложение с личным планом тренировок — в телефоне и на часах",
            en: "An app with your own workout plan — on your phone and watch",
        },
        listPriceCents: 4999,
        priceUnit: "year",
        status: "on_sale",
        buyRoute: "/buy-subscription",
        openRoute: "/my-revive",
        owned: { by: "subscription" },
        icon: "app",
        countable: true,
        order: 100,
    },
    {
        id: "tokens:sup",
        kind: "tokens",
        group: "app",
        title: { ru: "Токены", en: "Tokens" },
        gives: {
            ru: "Для умного поиска по лекциям и вопросов специалисту",
            en: "For smart search across the lectures and questions to a specialist",
        },
        listPriceCents: 1500,
        priceFrom: true,
        status: "on_sale",
        buyRoute: "/account/questions",
        openRoute: "/account/questions",
        // tok-10/20/35/60/120 и прежний «30 ответов».
        owned: { by: "quota", initialCounts: [10, 20, 35, 60, 120, 30] },
        icon: "tokens",
        countable: true,
        order: 110,
    },
    {
        // id прежний («…:year»): за него держатся модель баллов и аналитика. Годового
        // продукта в продаже нет — на странице специалиста токены и «Оплатить месяц»,
        // прежний «360 ответов / 1 год» за $30 из покупки снят. Поэтому одна честная
        // карточка «Вопросы специалисту»: что даёт, как платят, «Подробнее ›» — без
        // цены, без чипа и без пометок для владельца (29.09.2026).
        id: "specialist:year",
        kind: "specialist",
        group: "app",
        title: { ru: "Вопросы специалисту", en: "Questions to a specialist" },
        gives: { ru: "Специалист ответит вам лично в чате", en: "A specialist answers you personally in a chat" },
        sub: { ru: "за токены или помесячно", en: "with tokens or monthly" },
        listPriceCents: null,
        detailsOnly: true,
        status: "on_sale",
        // Владение — прежний годовой пакет (360 в quota.json).
        buyRoute: "/account/specialist-chat",
        openRoute: "/account/specialist-chat",
        owned: { by: "quota", initialCounts: [360] },
        icon: "specialist",
        countable: true,
        order: 120,
    },

    // ── Диагностика (2 в счёте + личная консультация вне счёта) ─────────────
    {
        id: "diag:diet",
        kind: "diagnostic",
        group: "diagnostics",
        title: { ru: "Диагностика диеты", en: "Diet diagnostics" },
        gives: { ru: "Персональный план питания", en: "A personal nutrition plan" },
        listPriceCents: DIET_CONSULTATION_AMOUNT,
        status: "on_sale",
        // Вкладка «Диета» (?tab=diet): без ?tab страница открывает «Рецепты».
        buyRoute: DIET_TAB_PATH,
        openRoute: DIET_TAB_PATH,
        owned: { by: "diagnostic", type: "diet" },
        icon: "diet",
        countable: true,
        order: 200,
    },
    {
        id: "diag:zog",
        kind: "diagnostic",
        group: "diagnostics",
        title: { ru: "Диагностика ЗОЖ", en: "Healthy-lifestyle diagnostics" },
        gives: {
            ru: "Ваши блоки напряжения, деревья и сфера жизни",
            en: "Your tension blocks, trees and life sphere",
        },
        listPriceCents: ZOG_CONSULTATION_AMOUNT,
        status: "on_sale",
        buyRoute: "/account/diet?tab=diagnostics",
        openRoute: "/account/diet?tab=diagnostics",
        owned: { by: "diagnostic", type: "zog" },
        icon: "zog",
        countable: true,
        order: 210,
    },
    {
        id: "consult:ogt",
        kind: "consultation",
        group: "diagnostics",
        title: { ru: "Личная консультация О. Г. Торсунова", en: "Personal consultation with Dr. O. G. Torsunov" },
        gives: { ru: "Ваш вопрос — лично доктору", en: "Your question — to the doctor in person" },
        listPriceCents: null,
        priceText: { ru: "от $1 550", en: "from $1,550" },
        status: "on_sale",
        buyRoute: "/personal-consultation",
        owned: { by: "none" },
        icon: "consult",
        // По записи — не входит в счёт (владелец может включить одной строкой).
        countable: false,
        order: 230,
    },

    // ── Товары для здоровья (2, скоро) ──────────────────────────────────────
    {
        id: "goods:nasal-spray",
        kind: "goods",
        group: "goods",
        title: { ru: "Назальный антивирусный спрей", en: "Nasal antiviral spray" },
        gives: { ru: "Спрей для носа на сезон простуд", en: "A nose spray for the cold season" },
        listPriceCents: null,
        status: "coming_soon",
        buyRoute: null,
        owned: { by: "none" },
        icon: "spray",
        countable: true,
        order: 300,
    },
    {
        // Семена на ухо бывают двух видов (владелец, 29.09.2026): эти — универсальные,
        // наш товар; индивидуальные подбирает центр «Амрита» (ayurveda:seeds ниже).
        // Это разные продукты, а не один и тот же в двух местах.
        id: "goods:ear-seeds",
        kind: "goods",
        group: "goods",
        title: { ru: "Семена на ухо — универсальные", en: "Ear seeds — universal" },
        gives: { ru: "Готовый набор для точек на ухе", en: "A ready-made set for ear points" },
        listPriceCents: null,
        status: "coming_soon",
        buyRoute: null,
        owned: { by: "none" },
        icon: "ear",
        countable: true,
        order: 310,
    },

    // ── Аюрведические продукты центра «Амрита» (5, вне счёта) ──────────────
    // Владелец, 29.09.2026: вкладка с torsunov-amrita.com. Цен на их сайте нет
    // («Узнать цену или заказать» — форма заявки), продаёт центр, не мы: ни цены,
    // ни чипа баллов (покупка там до нас не доходит), в опорный набор баллов не входят.
    // «Семена на ухо — индивидуальные» подбирает центр (владелец, 29.09.2026) — это
    // другой продукт, чем наши универсальные (goods:ear-seeds, скоро).
    ...(
        [
            [
                "bracelets",
                "Расслабляющие браслеты «Цветная и черно-белая схемы»",
                "Relaxing bracelets “Colour and black-and-white schemes”",
                "Индивидуально подобранные природные минералы",
                "Individually selected natural minerals",
            ],
            [
                "belt",
                "Расслабляющий пояс",
                "Relaxing belt",
                "Поддержка защитной системы и устойчивости к стрессу",
                "Support for the body’s defences and resilience to stress",
            ],
            [
                "seeds",
                "Семена на ухо — индивидуальные",
                "Ear seeds — individual",
                "Подбираются индивидуально в центре «Амрита»",
                "Selected individually at the Amrita centre",
            ],
            [
                "roots",
                "Корни",
                "Roots",
                "Способствуют очищению организма",
                "Help the body cleanse itself",
            ],
            [
                "scheme",
                "Расслабляющая схема",
                "Relaxing scheme",
                "Индивидуально подобранный сбор из природных компонентов",
                "An individually selected blend of natural ingredients",
            ],
        ] as const
    ).map(
        ([key, ru, en, givesRu, givesEn], i): Product => ({
            id: `ayurveda:${key}`,
            kind: "ayurveda",
            group: "ayurveda",
            title: { ru, en },
            gives: { ru: givesRu, en: givesEn },
            listPriceCents: null,
            // Владелец, 01.10.2026: ссылки на сайт центра пока убраны — «Скоро в продаже»
            // (без цены, без чипа, без ссылки). Вернуть: status "on_sale" + externalUrl: AMRITA_URL.
            status: "coming_soon",
            buyRoute: null,
            image: `/products/amrita/${key}.webp`,
            owned: { by: "none" },
            icon: "herb",
            countable: false,
            order: 350 + i,
        }),
    ),

    // ── Пакеты (вне счёта: дешевле купить то же самое) ──────────────────────
    // В каждом сегодняшнем пакете есть метод — видеокурс и/или пособие (карточки
    // PACKAGES и выдача сервера PACKAGE_CATALOG), поэтому у всех includesMethod:
    // тому, у кого метод уже есть, их не предлагаем. Сами пакеты не меняем —
    // «PDF + аудио», курс вместе с пособием — вопрос к владельцу (29.09.2026).
    ...(
        [
            ["pack-1-starter", "Пакет 1: Энергия для старта", "Package 1: Energy for the Start", 3590],
            ["pack-pdf-audio", "PDF + Аудио версии Метода", "PDF + Audio versions of the Method", 2790],
            ["pack-2-system", "Пакет 2: Системный подход", "Package 2: Systematic Approach", 10490],
            ["pack-pdf-audio-app", "PDF + Аудио + Приложение MY REVIVE", "PDF + Audio + MY REVIVE app", 7990],
            ["pack-3-mass", "Пакет 3: Масштабная энергия", "Package 3: Full-Scale Energy", 15490],
        ] as const
    ).map(
        ([id, ru, en, cents], i): Product => ({
            id: `package:${id}`,
            kind: "package",
            group: "packages",
            title: { ru, en },
            listPriceCents: cents,
            status: "on_sale",
            buyRoute: "/packages",
            owned: { by: "none" },
            icon: "package",
            countable: false,
            order: 400 + i,
            includesMethod: true,
        }),
    ),
];

const byOrder = (a: Product, b: Product) => a.order - b.order;

/**
 * ПАКЕТЫ «ПОИСК + ВОПРОСЫ СПЕЦИАЛИСТУ» (.com, features/supPlan; TECH_PLAN §10.2). Человеку на
 * пакетах «Токены» и «Вопросы специалисту» — одна карточка: пакет даёт и поиск на срок, и
 * вопросы. В реестр PRODUCTS она НЕ входит: «все продукты» модели баллов и их id
 * (tokens:sup, specialist:year — история баллов) не меняются; карточка лишь занимает место
 * этих двух на экране (withSupPlan). Своё — пока открыт оплаченный срок (вид кошелька).
 * Цена на карточке не пишется (как у всех): «от $9.90» — на шаге покупки, в окне пакетов,
 * куда ведёт кнопка (?buy=1). Баллы за покупку — по цене месячного пакета.
 */
export const SUP_PLAN_PRODUCT_ID = "sup:plan";
export const SUP_PLAN_REPLACES: readonly string[] = ["tokens:sup", "specialist:year"];

export const SUP_PLAN_PRODUCT: Product = {
    id: SUP_PLAN_PRODUCT_ID,
    kind: "tokens",
    group: "app",
    title: { ru: "Поиск и вопросы специалисту", en: "Search and specialist questions" },
    gives: {
        ru: "Умный поиск по лекциям и вопросы специалисту — на срок пакета",
        en: "Smart search across the lectures and specialist questions — for the pack's term",
    },
    listPriceCents: 990,
    priceFrom: true,
    status: "on_sale",
    buyRoute: "/account/specialist-chat?buy=1",
    openRoute: "/account/questions",
    owned: { by: "none" },
    icon: "tokens",
    countable: false,
    order: 110,
};

/** Человеку на пакетах — одна карточка пакета на месте «Токенов» и «Вопросов специалисту». */
export const withSupPlan = (list: Product[], plans: boolean): Product[] => {
    if (!plans) return list;
    const out: Product[] = [];
    let placed = false;
    for (const p of list) {
        if (!SUP_PLAN_REPLACES.includes(p.id)) {
            out.push(p);
            continue;
        }
        if (!placed) {
            out.push(SUP_PLAN_PRODUCT);
            placed = true;
        }
    }
    return out;
};

/**
 * Как сейчас заказать эту диагностику (diagnosticsOffer.ts, решение владельца
 * 28.09.2026); у остальных продуктов — null. Реестр при этом не меняется:
 * цена и страница остаются, чтобы вернуть заказ на сайт одним переключателем.
 * saleOf — как заказывать этому человеку (useDiagnosticSale: DIET_ORDER_EMAILS,
 * владелец 09.10.2026); без него — как всем.
 */
export const diagnosticSaleOf = (p: Product, saleOf: DiagnosticSaleOf = diagnosticSale): DiagnosticSale | null =>
    p.owned.by === "diagnostic" ? saleOf(p.owned.type) : null;

/** «Скоро в продаже»: по реестру — или диагностика, которую сейчас не заказать (этому человеку — saleOf). */
export const isComingSoon = (p: Product, saleOf: DiagnosticSaleOf = diagnosticSale): boolean =>
    p.status === "coming_soon" || diagnosticSaleOf(p, saleOf) === "soon";

/** Заказ на другом сайте (ЗОЖ — ogtorsunov.ru); null — заказывают у нас или никак. */
export const externalOrderUrl = (p: Product): string | null =>
    diagnosticSaleOf(p) === "external" ? ZOG_ORDER_URL : null;

/** «Все наши продукты»: 19 (16 в продаже, 3 скоро). */
export const countableProducts = (): Product[] => PRODUCTS.filter((p) => p.countable).sort(byOrder);

export const productById = (id: string): Product | undefined => PRODUCTS.find((p) => p.id === id);

/**
 * ТОВАРЫ ДЛЯ ЗДОРОВЬЯ И АЮРВЕДИЧЕСКИЕ ПРОДУКТЫ ПОКА СКРЫТЫ. Владелец, 03.10.2026:
 * «все аюрведические продукты нужно пока скрыть со всех вкладок сайта и
 * приложения, пока я не скажу их показать обратно». Один переключатель на весь
 * сайт: группы «Товары для здоровья» и «Аюрведические продукты» на «Все
 * продукты», полоса «Скоро в продаже», карточка семян на ухо в «Диагностике»,
 * спрей под поиском о простуде. Реестр PRODUCTS при этом прежний (модель баллов
 * и тесты читают его), картинки в public/ — тоже. Вернуть — true здесь и
 * AyurvedaProducts.SHOWN в приложении (v5).
 */
export const HEALTH_GOODS_SHOWN = false;

/** Группы, которые прячет переключатель (shown — его положение; по умолчанию — HEALTH_GOODS_SHOWN). */
const hiddenGroups = (shown: boolean): readonly ProductGroup[] => (shown ? [] : ["goods", "ayurveda"]);

/** Группа видна на сайте. */
export const isGroupShown = (g: ProductGroup, shown: boolean = HEALTH_GOODS_SHOWN): boolean =>
    !hiddenGroups(shown).includes(g);

/** Продукт можно показать человеку (его группа не скрыта переключателем выше). */
export const isProductShown = (p: Product, shown: boolean = HEALTH_GOODS_SHOWN): boolean => isGroupShown(p.group, shown);

/** Видимые продукты группы, по порядку показа (скрытые группы — пустые). */
export const productsInGroup = (group: ProductGroup, shown: boolean = HEALTH_GOODS_SHOWN): Product[] =>
    PRODUCTS.filter((p) => p.group === group && isProductShown(p, shown)).sort(byOrder);

/** Русский — свой, остальные языки сайта — английский. */
export const pickText = (t: LocalText, lang: string): string =>
    (lang || "").toLowerCase().startsWith("ru") ? t.ru : t.en;

export const productText = (
    p: Product,
    lang: string,
): { title: string; gives?: string; sub?: string; part?: string } => ({
    title: pickText(p.title, lang),
    gives: p.gives ? pickText(p.gives, lang) : undefined,
    sub: p.sub ? pickText(p.sub, lang) : undefined,
    part: p.part ? pickText(p.part, lang) : undefined,
});

/** Части по семействам, по порядку. */
export const productFamilies = (): Map<string, Product[]> => {
    const out = new Map<string, Product[]>();
    for (const p of [...PRODUCTS].sort(byOrder)) {
        if (!p.family) continue;
        const list = out.get(p.family) || [];
        list.push(p);
        out.set(p.family, list);
    }
    return out;
};

export const familyByKey = (key: string): ProductFamily | undefined =>
    PRODUCT_FAMILIES.find((f) => f.key === key);

/** «$14.90», «$300», «$49.99»: как на страницах покупки. */
export const formatUsd = (cents: number): string =>
    cents % 100 === 0 ? `$${cents / 100}` : `$${(cents / 100).toFixed(2)}`;
